import moment from 'moment';
import React, { useState } from 'react';
import { Avatar } from '../../../../../components';
import {
  DetailedAnalysisEmail,
  DetailedAnalysisEmailParticipant,
} from '../../../../../store/deals/deals.types';
import { DetailedHeaderPagination } from './pagination/DetailedHeaderPagination';

type Props = {
  data: {
    participants?: DetailedAnalysisEmailParticipant[];
    emails?: number;
    re?: string | null;
    lastActiveParticipant?: string;
    sentAt?: string;
    emailsForDateRange?: DetailedAnalysisEmail[];
    userId?: string;
    orgId?: string;
  };
  isAnimation?: boolean;
};

export const DetailedViewHeader: React.FC<Props> = ({
  data,
  isAnimation,
}): JSX.Element => {
  const {
    participants,
    emails,
    re,
    lastActiveParticipant,
    sentAt,
    emailsForDateRange,
  } = data;

  const [peopleTooltipOpen, setPeopleTooltipOpen] = useState(false);
  const [subjectTooltipOpen, setSubjectTooltipOpen] = useState(false);

  const togglePeopleTooltip = () => {
    setPeopleTooltipOpen(!peopleTooltipOpen);
  };

  const toggleSubjectTooltip = () => {
    setSubjectTooltipOpen(!subjectTooltipOpen);
  };

  const attrs = {
    wrapper: {
      className: 'detailed-view-header',
      style: isAnimation ? { overflow: 'hidden' } : {},
    },

    leftSection: {
      wrapper: {
        className: 'detailed-view-header__left',
      },

      subject: {
        wrapper: { className: 'detailed-view-header__left__subject' },
        title: { className: 'detailed-view-header__left__subject-title' },
        value: {
          className: 'detailed-view-header__left__subject-value',
          onMouseEnter: toggleSubjectTooltip,
          onMouseLeave: toggleSubjectTooltip,
        },
        tooltip: {
          className: subjectTooltipOpen
            ? 'open detailed-view-header__left__subject-toltip'
            : 'detailed-view-header__left__subject-toltip',
        },
      },
    },

    centralSection: {
      wrapper: { className: 'detailed-view-header__central' },
      people: {
        wrapper: {
          className: 'detailed-view-header__central__people',
          onMouseEnter: togglePeopleTooltip,
          onMouseLeave: togglePeopleTooltip,
        },
        name: {
          className: 'detailed-view-header__central__people-name',
        },

        tooltip: {
          wrapper: {
            className: peopleTooltipOpen
              ? 'open detailed-view-header__central__people__tooltip'
              : 'detailed-view-header__central__people__tooltip',
          },
          participant: {
            className:
              'detailed-view-header__central__people__tooltip__participant',
          },
          avatar: {
            className:
              'detailed-view-header__central__people__tooltip__participant-avatar',
            isTwentyPx: true,
          },
          name: {
            className:
              'detailed-view-header__central__people__tooltip__participant-name',
          },
        },
      },

      lastActive: {
        wrapper: {
          className: 'detailed-view-header__central__last-active',
        },

        value: {
          className: 'detailed-view-header__central__last-active-value',
        },

        tooltip: {
          className: 'detailed-view-header__central__last-active-tooltip',
        },
      },

      messageQty: {
        wrapper: {
          className: 'detailed-view-header__central__message-qty',
        },

        tooltip: {
          className: 'detailed-view-header__central__message-qty-tooltip',
        },
      },

      date: {
        className: 'detailed-view-header__central-date',
        style:
          emailsForDateRange && emailsForDateRange.length > 1
            ? { maxWidth: '36%' }
            : {},
      },
    },

    rightSection: {
      wrapper: {
        className: 'detailed-view-header__right',
      },
    },
  };

  const allParticipants = participants
    ?.map((el) => el?.metadata?.name?.split(' ')[0] || el.metadata.name)
    .join(', ');

  const emailsQty = emails || 0;

  const emailSubject =
    re?.length && re.split(':').length > 1 ? re.split(':')[1] : re;

  const subjectTooltip = (
    <span {...attrs.leftSection.subject.tooltip}>{emailSubject}</span>
  );

  const lastActivePrt = lastActiveParticipant || '';

  const getTimeAgo = () => {
    const dateForDiff = sentAt?.length ? moment(sentAt) : '';

    const timeFromNow = sentAt?.length ? moment(dateForDiff).fromNow() : '';

    return { timeFromNow };
  };

  const { timeFromNow } = getTimeAgo();

  const getParticipantSection = () => {
    const participantsString = allParticipants;
    let tooltip: JSX.Element | null = null;
    if (participants?.length && allParticipants?.length) {
      const tooltipData = (
        <div {...attrs.centralSection.people.tooltip.wrapper}>
          {participants.map((el) => (
            <div
              {...attrs.centralSection.people.tooltip.participant}
              key={el.email}
            >
              <Avatar
                image={el?.metadata.avatarSrc ? el?.metadata.avatarSrc : ''}
                userName={el?.metadata.name}
                {...attrs.centralSection.people.tooltip.avatar}
              />
              <span {...attrs.centralSection.people.tooltip.name}>
                {el.metadata.name}
              </span>
            </div>
          ))}
        </div>
      );
      tooltip = tooltipData;
    }
    return { participantsString, tooltip };
  };

  const { participantsString, tooltip } = getParticipantSection();

  const subjectSection = (
    <div {...attrs.leftSection.wrapper}>
      <div {...attrs.leftSection.subject.wrapper}>
        <span {...attrs.leftSection.subject.title}>Re:</span>
        {subjectTooltip}
        <span {...attrs.leftSection.subject.value}>{emailSubject}</span>
      </div>
    </div>
  );

  const centralSection =
    data && data.participants?.length ? (
      <div {...attrs.centralSection.wrapper}>
        <div {...attrs.centralSection.people.wrapper}>
          {tooltip}
          <span {...attrs.centralSection.people.name}>
            {participantsString}
          </span>
        </div>
        <div {...attrs.centralSection.lastActive.wrapper}>
          <span {...attrs.centralSection.lastActive.value}>
            {lastActivePrt} ({timeFromNow})
          </span>
          <div {...attrs.centralSection.lastActive.tooltip}>
            Last interaction in the thread
          </div>
        </div>
        <div {...attrs.centralSection.messageQty.wrapper}>
          {emailsQty}{' '}
          <div {...attrs.centralSection.messageQty.tooltip}>
            Total messages in the thread {emailsQty}
          </div>
        </div>
      </div>
    ) : null;

  const paginationSection = (
    <div {...attrs.rightSection.wrapper}>
      <DetailedHeaderPagination />
    </div>
  );

  return (
    <div {...attrs.wrapper}>
      {subjectSection}
      {centralSection}
      {paginationSection}
    </div>
  );
};
